import { Directive, ElementRef, HostListener, AfterViewInit } from '@angular/core';
import {EventService} from "@cybfed/core/store/event.service";

@Directive({
    standalone: true,
    selector: '[appDynamicScale]'
})
export class DynamicScaleDirective implements AfterViewInit {
    constructor(private el: ElementRef, private eventManager: EventService) {}

    ngAfterViewInit(): void {
        // Use a timeout to ensure that the view has been rendered and sizes are available
        this.eventManager.rescale.subscribe((date) => {
            setTimeout(() => {
                this.scaleContent();
            },100);
        })
    }

    @HostListener('window:resize')
    onResize() {
        this.scaleContent();
    }

    private scaleContent(): void {
        const container: HTMLElement = this.el.nativeElement;

        // Ensure container has dimensions; if not, exit early.
        if (!container.clientWidth || !container.clientHeight) {
            console.warn('Container dimensions are zero.');
            this.eventManager.rescaleDone.emit();
            return;
        }

        // Look for the inner content wrapper with class "scaler"
        const inner = container.querySelector('.scaler') as HTMLElement;
        if (!inner) {
            console.warn('No element with class "scaler" found inside the container.');
            this.eventManager.rescaleDone.emit();
            return;
        }

        // Reset any previous transforms to get the natural dimensions
        inner.style.transform = '';

        // Get container dimensions (the available area)
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;

        // Get the natural dimensions of the inner content
        const contentWidth = inner.scrollWidth;
        const contentHeight = inner.scrollHeight;

        // Check that inner content dimensions are non-zero
        if (contentWidth === 0 || contentHeight === 0) {
            console.warn('Inner content dimensions are zero.');

            this.eventManager.rescaleDone.emit();
            return;
        }

        // Calculate the scale factor required to fit the content entirely within the container.
        const scaleX = containerWidth / contentWidth;
        const scaleY = containerHeight / contentHeight;
        const scale = Math.min(scaleX, scaleY, 1); // Do not scale above 1 (i.e. scaling up)

        // If scale becomes 0 (shouldn't happen with our checks), warn and exit.
        if (scale <= 0) {
            console.warn('Computed scale is 0 or less.');

            this.eventManager.rescaleDone.emit();
            return;
        }

        // Apply the scaling transform to the inner content.
        inner.style.transform = `scale(${scale})`;
        // Set transform origin to top left so scaling doesn't interfere with centering
        inner.style.transformOrigin = 'top left';
        inner.style.width = `${100 / scale}%`
        inner.style.height = `${100 / scale}%`

        // Center the scaled content within the container using the effective scale.
        const offsetX = (containerWidth - contentWidth * scale) / 2;
        const offsetY = (containerHeight - contentHeight * scale) / 2;

        inner.style.position = 'absolute';
        inner.style.left = `0px`;
        inner.style.top = `0px`;
        this.eventManager.rescaleDone.emit();
    }
}
